import { z } from "zod"

import { selectableDocumentTypeKeys } from "../../registries.js"
import { validateExternalHandoffUrl } from "../../client-engagements.js"
import { portalItemListProblem } from "../../portal-item-answers.js"
import { portalClientSlugProblem } from "../../portal-copy.js"
import { ClientBrandFieldsSchema } from "./client-brand.js"
import { transactionQuestionAskOnlyMessage } from "../../transaction-questions.js"
import {
  PortalItemOptionsInputSchema, PortalItemShowWhenInputSchema, PortalRequestItemKindSchema, checkPortalShowWhen,
} from "./portal-items.js"
import { ItemSignatureInputSchema } from "./signatures.js"

export const EngagementStateSchema = z.enum(["onboarding", "active", "paused", "ended"])
export const ApiTransitionEngagementBodySchema = z.object({
  idempotencyKey: z.uuid(), expectedState: EngagementStateSchema,
  state: z.enum(["active", "paused", "ended"]), reason: z.string().trim().min(1).max(2000),
})
export const ApiCancelEngagementRequestBodySchema = z.object({
  idempotencyKey: z.uuid(), reason: z.string().trim().min(1).max(2000),
})
export const AccountingProviderSchema = z.enum(["quickbooks", "xero", "other", "none"])
export const EngagementActionResponseSchema = z.object({
  provider: AccountingProviderSchema.optional(),
  reportedComplete: z.boolean(),
})
/** What the firm reads back: the client's answer, plus the time LedgerBox
 * marked it done because the client's company connected. Never accepted from
 * a client. */
export const EngagementActionResponseReadSchema = EngagementActionResponseSchema.extend({
  connectedAt: z.iso.datetime().optional(),
})

export const EngagementActionConfigSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("accounting_access"),
    accountantEmail: z.email().max(254).optional(),
    quickbooksFirmId: z.string().trim().min(1).max(120).optional(),
    instructions: z.string().trim().max(2000).optional(),
  }),
  z.object({
    type: z.literal("external_handoff"),
    category: z.enum(["agreement", "payment"]),
    url: z.url().max(2048),
    expired: z.boolean().optional(),
  }),
])

export const EngagementItemInputSchema = z.object({
  templateItemId: z.uuid().nullable().optional(),
  kind: PortalRequestItemKindSchema,
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).nullable().optional(),
  required: z.boolean(),
  options: PortalItemOptionsInputSchema,
  expectedTypes: z.array(z.enum(selectableDocumentTypeKeys)).max(selectableDocumentTypeKeys.length).default([]),
  accountHint: z.string().trim().max(120).nullable().optional(),
  periodMode: z.enum(["request_period", "any", "explicit"]).default("any"),
  explicitPeriod: z.string().regex(/^\d{4}-(?:0[1-9]|1[0-2])$/).nullable().optional(),
  allowMultiple: z.boolean().default(false),
  actionConfig: EngagementActionConfigSchema.nullable().optional(),
  exampleFileId: z.uuid().nullable().optional(),
  signature: ItemSignatureInputSchema.optional(),
  showWhen: PortalItemShowWhenInputSchema.nullable().optional(),
}).superRefine((item, context) => {
  // No token keeps the PDF the item had on a saved draft; with none, the
  // API answers signature_original_required.
  if (item.kind === "signature" && !item.signature) {
    context.addIssue({ code: "custom", path: ["signature"], message: "Choose the PDF to sign." })
  }
  if (item.kind !== "signature" && item.signature) {
    context.addIssue({ code: "custom", path: ["signature"], message: "Only a document to sign can have a PDF to sign." })
  }
  const listProblem = portalItemListProblem(item.kind, item.options)
  if (listProblem) context.addIssue({ code: "custom", path: ["options"], message: listProblem })
  if (item.kind !== "document" && item.expectedTypes.length > 0) {
    context.addIssue({ code: "custom", path: ["expectedTypes"], message: "Only document items can name document types." })
  }
  if (item.periodMode === "explicit" && !item.explicitPeriod) {
    context.addIssue({ code: "custom", path: ["explicitPeriod"], message: "Choose a month." })
  }
  if (item.kind === "accounting_access" && item.actionConfig?.type !== "accounting_access") {
    context.addIssue({ code: "custom", path: ["actionConfig"], message: "Add accounting access instructions." })
  }
  if (item.kind === "external_handoff" && item.actionConfig?.type !== "external_handoff") {
    context.addIssue({ code: "custom", path: ["actionConfig"], message: "Add a handoff link." })
  }
  if (!["accounting_access", "external_handoff"].includes(item.kind) && item.actionConfig) {
    context.addIssue({ code: "custom", path: ["actionConfig"], message: "This item cannot have an action." })
  }
  // Listed so a request's items type-check as engagement items; an engagement
  // request never holds a question, so no engagement input can make one.
  if (item.kind === "transaction_question") {
    context.addIssue({ code: "custom", path: ["kind"], message: transactionQuestionAskOnlyMessage })
  }
})

export const EngagementItemsInputSchema = z.array(EngagementItemInputSchema).min(1).max(50).refine(
  (items) => new Set(items.map((item) => item.name.toLowerCase())).size === items.length,
  "Item names must be unique."
).superRefine(checkPortalShowWhen)

const ActivationRecipientSchema = z.object({
  name: z.string().trim().min(1).max(120), email: z.email().max(254),
})

/** The client's readable link segment, as the firm typed it. */
const ActivationClientSlugSchema = z.string().trim().toLowerCase().superRefine((slug, context) => {
  const problem = portalClientSlugProblem(slug)
  if (problem) context.addIssue({ code: "custom", message: problem })
})

const ActivationDetailsSchema = z.object({
  idempotencyKey: z.uuid(),
  workspaceId: z.uuid().optional(),
  draftEngagementId: z.uuid().optional(),
  confirmLegacyClient: z.boolean().default(false),
  clientName: z.string().trim().min(1).max(120),
  /** Omitted takes the request's name. */
  serviceDescription: z.string().trim().min(1).max(2000).optional(),
  /** Omitted takes the request list's name. */
  title: z.string().trim().min(1).max(120).optional(),
  templateId: z.uuid().nullable().optional(),
  items: EngagementItemsInputSchema,
  coverageStart: z.iso.date().nullable().optional(),
  coverageEnd: z.iso.date().nullable().optional(),
  dueAt: z.iso.datetime().nullable().optional(),
  /** More people at the client. They are saved with the client and get no
      email now; the firm invites them from the client's Contacts. */
  contacts: z.array(ActivationRecipientSchema).max(10).optional(),
  /** The readable link. Omitted builds one from the client name; a number is
      added when another client of the firm has it. */
  clientSlug: ActivationClientSlugSchema.optional(),
}).extend(ClientBrandFieldsSchema.shape)

export const ApiActivateClientEngagementBodySchema = z.discriminatedUnion("mode", [
  ActivationDetailsSchema.extend({
    mode: z.literal("invite"), recipient: ActivationRecipientSchema,
    confirmIdentity: z.literal(true), confirmRecipient: z.literal(true), confirmRequest: z.literal(true),
  }),
  ActivationDetailsSchema.extend({
    mode: z.literal("draft"), recipient: ActivationRecipientSchema.optional(),
    confirmIdentity: z.boolean().default(false), confirmRecipient: z.boolean().default(false),
    confirmRequest: z.boolean().default(false),
  }),
  z.object({ idempotencyKey: z.uuid(), mode: z.literal("skip") }),
]).superRefine((body, context) => {
  if (body.mode === "skip") return
  if (body.draftEngagementId && !body.workspaceId) {
    context.addIssue({ code: "custom", path: ["workspaceId"], message: "Choose the client that owns this draft." })
  }
  if (body.coverageStart && body.coverageEnd && body.coverageStart > body.coverageEnd) {
    context.addIssue({ code: "custom", path: ["coverageEnd"], message: "Coverage end must follow coverage start." })
  }
  const emails = [body.recipient?.email, ...(body.contacts ?? []).map((contact) => contact.email)]
    .filter((email): email is string => Boolean(email)).map((email) => email.trim().toLowerCase())
  if (new Set(emails).size !== emails.length) {
    context.addIssue({ code: "custom", path: ["contacts"], message: "Each contact needs a different email." })
  }
})
export type ApiActivateClientEngagementBody =z.infer<typeof ApiActivateClientEngagementBodySchema>

export const ApiCreateManualEngagementRequestBodySchema = z.object({
  idempotencyKey: z.uuid(),
  title: z.string().trim().min(1).max(120),
  templateId: z.uuid().nullable().optional(),
  items: EngagementItemsInputSchema,
  coverageStart: z.iso.date().nullable().optional(),
  coverageEnd: z.iso.date().nullable().optional(),
  dueAt: z.iso.datetime().nullable().optional(),
  confirmRequest: z.literal(true),
  send: z.literal(true),
}).superRefine((body, context) => {
  if (body.coverageStart && body.coverageEnd && body.coverageStart > body.coverageEnd) {
    context.addIssue({ code: "custom", path: ["coverageEnd"], message: "Coverage end must follow coverage start." })
  }
})
export type ApiCreateManualEngagementRequestBody = z.infer<typeof ApiCreateManualEngagementRequestBodySchema>

export const ApiStartClientEngagementBodySchema = z.object({
  idempotencyKey: z.uuid(),
  expectedVersions: z.array(z.object({ itemId: z.uuid(), evidenceVersion: z.number().int().min(1) })).max(50),
}).refine((body) => new Set(body.expectedVersions.map((item) => item.itemId)).size === body.expectedVersions.length,
  "Each item can be reviewed once.")
export type ApiStartClientEngagementBody = z.infer<typeof ApiStartClientEngagementBodySchema>

export const ApiReviewEngagementItemBodySchema = z.object({
  idempotencyKey: z.uuid(),
  action: z.enum(["verify", "waive", "reject", "reopen", "help", "resume", "replace_handoff", "expire_handoff"]),
  expectedVersion: z.number().int().min(1),
  reason: z.string().trim().min(1).max(2000).optional(),
  handoffUrl: z.url().max(2048).optional(),
}).superRefine((body, context) => {
  if (["waive", "reject", "reopen", "help", "replace_handoff", "expire_handoff"].includes(body.action) && !body.reason) {
    context.addIssue({ code: "custom", path: ["reason"], message: "Give a reason." })
  }
  if (body.action === "replace_handoff" && !body.handoffUrl) {
    context.addIssue({ code: "custom", path: ["handoffUrl"], message: "Enter the replacement link." })
  }
})
export type ApiReviewEngagementItemBody = z.infer<typeof ApiReviewEngagementItemBodySchema>

export const ApiEngagementActionResponseBodySchema = z.object({
  idempotencyKey: z.uuid(),
  expectedVersion: z.number().int().min(1),
  response: EngagementActionResponseSchema,
})
export type ApiEngagementActionResponseBody = z.infer<typeof ApiEngagementActionResponseBodySchema>

export const ApiMonthlyScheduleBodySchema = z.object({
  idempotencyKey: z.uuid(),
  templateId: z.uuid(),
  frequency: z.literal("monthly"),
  timeZone: z.string().trim().min(1).max(100),
  firstCollectionMonth: z.string().regex(/^\d{4}-(?:0[1-9]|1[0-2])$/),
  requestDay: z.number().int().min(1).max(31),
  sendHour: z.number().int().min(0).max(23),
  dueDay: z.number().int().min(1).max(31),
}).superRefine((body, context) => {
  if (body.dueDay < body.requestDay) context.addIssue({ code: "custom", path: ["dueDay"], message: "Due day must be on or after request day." })
  try { new Intl.DateTimeFormat("en-US", { timeZone: body.timeZone }).format(new Date()) }
  catch { context.addIssue({ code: "custom", path: ["timeZone"], message: "Choose a valid IANA time zone." }) }
})
export type ApiMonthlyScheduleBody = z.infer<typeof ApiMonthlyScheduleBodySchema>

/** Call with the admin-owned allowlist before persisting a handoff item. */
export const validateEngagementHandoffItems = (
  items: readonly z.infer<typeof EngagementItemInputSchema>[],
  approvedDomains: readonly string[]
): { valid: true } | { valid: false; itemIndex: number; reason: string } => {
  for (const [itemIndex, item] of items.entries()) {
    if (item.actionConfig?.type !== "external_handoff") continue
    const result = validateExternalHandoffUrl(item.actionConfig.url, approvedDomains)
    if (!result.valid) return { valid: false, itemIndex, reason: result.reason }
  }
  return { valid: true }
}
